import { Button, Space, Typography, Popconfirm } from "antd";
import ListTable from "common/ListTable";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { cglFormatDate, hasPermission } from "utils/functions";
import { getData, deleteData } from "../../../../apiServices/common";
import AppContent from "../../../../common/AppContent";
import HandleNotification from "../../../../common/Notification";
import AppPageHeader from "../../../../common/PageHeader";
import ExtraContent from "../../../Configuration/CommonComponents/ExtraContent";
import { IMPORT_MACHINE_INFO } from "apiServices/API_ENDPOINTS";
import { MACHINE_INFORMATION_IMPORT_PERMISSION } from "routes/permissions";
import { useSearchParams } from "react-router-dom";
import { getTypes } from "./utils/getTypes";

const MaintenaceConfig = (props) => {
  const { type: maintenanceType } = props;
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  const navigate = useNavigate();
  const { id } = useParams();

  const splittedFullPath = routerPath.split("/");
  const type = splittedFullPath[splittedFullPath.length - 1];

  const formType = useMemo(() => {
    getTypes(type || maintenanceType);
    return getTypes(type || maintenanceType);
  }, [type, maintenanceType]);

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState();

  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [filterValues, setFilterValues] = useState({});
  const [error, setError] = useState(null);

  // Antd
  const { Title } = Typography;

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const onFilterChange = (values) => {
    setFilterValues(values);
  };

  const fetchData = useCallback(
    async (filterValues) => {
      setError(null);
      const query = `/api/digitalization/machine-maintenance/${formType}`;
      const bodyData = {
        page: page,
        per_page: filterValues?.per_page || 10,
        ...(filterValues?.name && { name: filterValues?.name }),
        // ...(filterValues?.style_id && { style_id: filterValues?.style_id }),
        // ...(filterValues?.po_map_id && { po_map_id: filterValues?.po_map_id }),
      };

      const response = await getData(query, false, bodyData);

      if (response && response?.data?.code === 200) {
        setTableData(response?.data?.data?.data);
        setPageSize(response?.data?.data?.per_page);
        setTotal(response?.data?.data?.total);
      } else {
        HandleNotification("error", "Error", "Something went wrong");
        setError("Could not load this list.");
      }
    },
    [page, formType],
  );

  useEffect(() => {
    const value = {
      ...filterValues,
      page: page,
    };
    fetchData(value);
  }, [refresh, page, pageSize, filterValues, fetchData]);

  useEffect(() => {
    setFilterValues({});
    setPage(1);
    setPageSize(10);
    setTotal(0);
    setRefresh((prev) => prev + 1);
  }, [routerPath, formType]);

  const onPageChange = (newPage) => {
    setPage(newPage);
  };

  const handleEdit = (event, record) => {
    const url = `/digitalization/machine-maintenance/configurations/${maintenanceType}/${event}/${record.id}`;
    navigate(url);
  };

  const handleDelete = async (record) => {
    try {
      const response = await deleteData(
        `/api/digitalization/machine-maintenance/${formType}/${record.id}`,
      );

      if (response?.code === 200) {
        HandleNotification(
          "success",
          "Deleted",
          "Machine information deleted successfully",
        );
        setRefresh((prev) => prev + 1);
      }
    } catch (error) {
      HandleNotification(
        "error",
        "Error",
        "Failed to delete machine information",
      );
    }
  };

  const tableColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 200,
      render: (status) => (status === 1 ? "Active" : "Inactive"),
    },
    {
      title: "Action",
      key: "action",
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            style={{ marginLeft: 5 }}
            size="small"
            onClick={() => handleEdit("edit", record)}
          >
            Edit
          </Button>
          <Button
            style={{ marginLeft: 5 }}
            size="small"
            onClick={() => handleEdit("view", record)}
          >
            View
          </Button>
          <Popconfirm
            title="Are you sure to delete this item?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button style={{ marginLeft: 5 }} size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={onFilterChange}
        filterName={"machine-configurations"}
        setShowFilter={setShowFilter}
        showFilter={showFilter}
      />
    ),
    [showFilter, tableData],
  );

  const addOrUpdateRoute = () => {
    const route = id
      ? `/digitalization/machine-maintenance/configurations/${maintenanceType}/edit/${id}`
      : `/digitalization/machine-maintenance/configurations/${maintenanceType}/store`;
    navigate(route);
  };

  const header = (
    <AppPageHeader
      extra={
        <ExtraContent
          refresh={refresh}
          setRefresh={setRefresh}
          handleAddItemOpen={addOrUpdateRoute}
          setShowFilter={setShowFilter}
          addButtonText={
            hasPermission([MACHINE_INFORMATION_IMPORT_PERMISSION])
              ? "Add New"
              : "No Permission"
          }
          showFilter={showFilter}
        />
      }
      title={
        <Title level={5} style={{ textTransform: "capitalize" }}>
          {maintenanceType} Information
        </Title>
      }
    />
  );
  const content = (
    <>
      {memoizedCustomFilter}
      <ListTable
        columns={tableColumns}
        dataSource={tableData}
        loading={!tableData}
        error={error}
        onRetry={() => fetchData({ ...filterValues, page })}
        emptyText="No records to show yet."
        pagination={{
          showSizeChanger: false,
          total: total,
          current: page,
          pageSize: pageSize,
          onChange: onPageChange,
        }}
      />
    </>
  );
  return (
    <>
      <AppContent header={header} content={content} />
    </>
  );
};

export default MaintenaceConfig;
